import React, { useState, useEffect } from "react";
import axios from "axios";
import { Modal, Button, Form, Tab, Tabs, Table } from "react-bootstrap";
import swal from "sweetalert";
import DataTable from "react-data-table-component";
import { customStyles } from "../../../../assets/table-style";
import "@fortawesome/fontawesome-free/css/all.min.css";
import BASE_URL from "../../../../assets/global/url";
import Select from "react-select";
import { Link, useParams, useNavigate } from "react-router-dom";
import { CSSTransition } from "react-transition-group";
import "../../../../assets/css/style.css";
import DatePicker from "react-datepicker";
import NoAccess from "../../../../assets/img/NoAccess.png";
import ProductTab from "./InventoryReport/ProductTab";
import { constant_productCategory } from "../../../../constants/productOptions";
import { useQueryParams } from "../../../../hooks/customHook/useQueryParams";
import { ProductDeficitTab } from "./InventoryReport/ProductDeficitTab";
import { exportExcelDeficitStock } from "../../../../utils/exportCSV/exportExcelDeficitStock";
import {
  formatToTwoDecimal,
  parseNumber,
} from "../../../../utils/numberFormatter";

const InventoryReport1 = ({ authrztn }) => {
  const navigate = useNavigate();

  // Tab Navigation States
  const [mainTab, setMainTab] = useState("Stocks");
  const [stocksTab, setStocksTab] = useState("Raw Materials");
  const [deficitTab, setDeficitTab] = useState("Raw Materials");

  // For query parameters
  const queryParameterList = ["module", "id", "fromDate", "toDate"];
  const queryParameters = useQueryParams(queryParameterList);
  const { module, id, fromDate, toDate } = queryParameters;

  // For Cutoff filter
  const [thisFromdate, setThisFromdate] = useState("");
  const [thisTodate, setThisTodate] = useState("");
  const [cutoffList, setCutoffList] = useState([]);
  const [selectedCutoff_id, setSelectedCutoff_id] = useState("");

  // For Overview
  const [overview, setOverview] = useState({});
  const [showTable, setShowTable] = useState(false);
  const toggleTable = () => {
    setShowTable(!showTable);
  };

  // Helper: calculate the growth index
  const getGrowthIndex = ({ current = 0, previous = 0 }) => {
    const normalizedCurrent = parseNumber(current);
    const normalizedPrevious = parseNumber(previous);

    if (!normalizedPrevious) return 0;

    return (
      ((normalizedCurrent - normalizedPrevious) / normalizedPrevious) * 100
    );
  };

  // Get inventory report overview
  const getOverview = async (startDate, endDate) => {
    try {
      const res = await axios.get(`${BASE_URL}/inventoryReport/overview`, {
        params: {
          startDate,
          endDate,
        },
      });

      setOverview(res.data.overview);
    } catch (error) {
      console.error(error);
    }
  };

  // Get the default cutoff
  const getCutoff = async () => {
    await axios
      .get(`${BASE_URL}/cutoff/getCutoffs`)
      .then((res) => {
        if (!res.data.length) return; // No cutoff records yet

        const [{ id: cutoffId, from: startDate, to: endDate }] = res.data; // Destructure the first item in array

        // Use query parameters if provided; otherwise, fall back to default values
        const defaultCutoffId = id || cutoffId;
        const cutoffStartDate = fromDate || startDate;
        const cutoffEndDate = toDate || endDate;

        setCutoffList(res.data);
        setSelectedCutoff_id(defaultCutoffId);
        setThisFromdate(cutoffStartDate);
        setThisTodate(cutoffEndDate);
        getOverview(cutoffStartDate, cutoffEndDate);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  // Handle cutoff change
  const handleCutoffChange = (value) => {
    setSelectedCutoff_id(value);
    const cutoff_fromdate =
      cutoffList.find((cutoff) => String(cutoff.id) === String(value)).from ||
      "";
    const cutoff_todate =
      cutoffList.find((cutoff) => String(cutoff.id) === String(value)).to || "";

    setThisFromdate(cutoff_fromdate);
    setThisTodate(cutoff_todate);

    getOverview(cutoff_fromdate, cutoff_todate);
  };

  // For product tab props
  const dateRange = {
    thisFromdate,
    thisTodate,
  };

  useEffect(() => {
    getCutoff();
  }, []);

  const exportAsExcel = async () => {
    if (mainTab !== "Deficit Stocks") {
      swal({
        icon: "warning",
        title: "Wrong Tab Selected",
        text: 'Please navigate to the "Deficit Stocks" tab before exporting.',
      });
      return;
    }

    const cutoff = cutoffList?.find(
      (c) => String(c.id) === String(selectedCutoff_id),
    );
    const cutoffName = cutoff?.name ?? "N/A";
    const safeFileName = `Inventory_Report_Deficit_Stocks_${cutoffName}`;

    try {
      swal({
        title: "Generating Export...",
        text: "Please wait while we prepare your file.",
        icon: "info",
        buttons: false,
        closeOnClickOutside: false,
        closeOnEsc: false,
      });

      await exportExcelDeficitStock({
        fileName: safeFileName,
        startDate: thisFromdate,
        endDate: thisTodate,
        productCategory: deficitTab,
      });

      swal({
        icon: "success",
        title: "Export Successful",
        text: "Your file has been downloaded successfully.",
        timer: 3000,
        buttons: false,
      });
    } catch (error) {
      console.error("[exportAsExcel] Export failed:", error);
      swal({
        icon: "error",
        title: "Export Error",
        text: "Failed to export data. Please try again.",
      });
    }
  };

  return (
    <div className="h-100 w-100 bg-white p-2">
      <>
        <div className="w-100 d-flex flex-row justify-content-between">
          <div className="d-flex flex-column title-custom">
            <span className="fs-3 text-uppercase">
              {module && id && fromDate && toDate && module === "cutoff" && (
                <Link
                  to={`/accounting/view-cutoff/${id}`}
                  className="text-dark mx-2"
                >
                  <i class="fa-solid fa-arrow-left"></i>
                </Link>
              )}
              {module && id && module === "earnings" && (
                <Link
                  to={`/accounting/view-earnings/${id}`}
                  className="text-dark mx-2"
                >
                  <i class="fa-solid fa-arrow-left"></i>
                </Link>
              )}
              Inventory Report
            </span>
          </div>

          {/* Overview Button and Export */}
          <div className="d-flex gap-2 align-items-stretch">
            <button className="btn btn-primary" onClick={toggleTable}>
              {showTable ? "Hide Overview" : "Show Overview"}
            </button>

            {authrztn?.includes("InventoryReport-IE") && (
              <div className="dropdown">
                <button
                  className="btn btn-outline-danger dropdown-toggle h-100"
                  type="button"
                  data-bs-toggle="dropdown"
                  aria-expanded="false"
                >
                  Export Options
                </button>

                <ul className="dropdown-menu">
                  <li>
                    <button className="dropdown-item" onClick={exportAsExcel}>
                      CSV Format
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Cutoff filter */}
        <div className="w-100 row mx-auto mt-2">
          <h6>Accounting Period</h6>
          <div className="col-sm mb-2">
            <span>Cutoff</span>
            <Form.Select
              value={selectedCutoff_id}
              onChange={(e) => handleCutoffChange(e.target.value)}
              className="form-select"
              onMouseDown={(e) => {
                if (cutoffList.length === 0) {
                  e.preventDefault();
                  swal({
                    icon: "warning",
                    title: "No Cutoff Found",
                    text: "No cutoff records found. Please create a cutoff first in Monthly Cutoff Module.",
                  });
                  return;
                }
              }}
            >
              {cutoffList.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </Form.Select>
          </div>
          <div className="col-sm mb-2">
            <span>From</span>
            <DatePicker
              selected={thisFromdate}
              dateFormat="MMM/dd/yyyy"
              className="form-control"
              readOnly
            />
          </div>
          <div className="col-sm mb-2">
            <span>To</span>
            <DatePicker
              selected={thisTodate}
              dateFormat="MMM/dd/yyyy"
              className="form-control"
              readOnly
            />
          </div>
          <div className="col-sm"></div>
          <div className="col-sm"></div>
        </div>

        {/* Overview Table */}
        <div className="w-100 container-fluid mt-2">
          {/* <div className="d-flex justify-content-between align-items-center">
            <h5>Overview</h5>
            <button className="btn btn-primary" onClick={toggleTable}>
              {showTable ? "Hide Overview" : "Show Overview"}
            </button>
          </div> */}

          <div className="d-flex justify-content-between align-items-center">
            <h5>Overview</h5>
          </div>

          <CSSTransition
            in={showTable}
            timeout={300}
            classNames="slide"
            unmountOnExit
          >
            <div className="table-responsive mt-3">
              <table className="table table-bordered table-striped">
                <thead className="thead-light">
                  <tr>
                    <th rowSpan="2">Inventory Type</th>
                    <th colSpan="3" className="text-center">
                      Final Inventory
                    </th>
                    <th colSpan="3" className="text-center">
                      Comparison to Previous Period
                    </th>
                    <th colSpan="2" className="text-center">
                      Final Inventory Growth Index
                    </th>
                  </tr>
                  <tr>
                    <th>Quantity</th>
                    <th>Avg. Price</th>
                    <th>Amount (₱)</th>
                    <th>Quantity</th>
                    <th>Avg. Price</th>
                    <th>Amount (₱)</th>
                    <th>Quantity (%)</th>
                    <th>Amount (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(overview).map(([category, value], index) => {
                    // prettier-ignore
                    const inventoryTotals = {
                      beginningInventoryQuantity: formatToTwoDecimal(value.beginningInventoryQuantity),
                      beginningInventoryAveragePrice: formatToTwoDecimal(value.beginningInventoryAveragePrice),
                      beginningInventoryAmount: formatToTwoDecimal(value.beginningInventoryAmount),
                      finalInventoryQuantity: formatToTwoDecimal(value.finalInventoryQuantity),
                      finalInventoryAveragePrice: formatToTwoDecimal(value.finalInventoryAveragePrice),
                      finalInventoryAmount: formatToTwoDecimal(value.finalInventoryAmount),
                    }

                    const quantityGrowthIndex = getGrowthIndex({
                      current: inventoryTotals.finalInventoryQuantity,
                      previous: inventoryTotals.beginningInventoryQuantity,
                    });

                    const amountGrowthIndex = getGrowthIndex({
                      current: inventoryTotals.finalInventoryAmount,
                      previous: inventoryTotals.beginningInventoryAmount,
                    });

                    return (
                      // prettier-ignore
                      <React.Fragment>
                        <tr key={index}>
                          <td>{category} Inventory</td>
                          {/* For Current Period */}
                          <td>{inventoryTotals.finalInventoryQuantity}</td>
                          <td>{inventoryTotals.finalInventoryAveragePrice}</td>
                          <td>{inventoryTotals.finalInventoryAmount}</td>
                          {/* Comparison for Previous Period */}
                          <td>{inventoryTotals.beginningInventoryQuantity}</td>
                          <td>{inventoryTotals.beginningInventoryAveragePrice}</td>
                          <td>{inventoryTotals.beginningInventoryAmount}</td>
                          {/* For Growth Index */}
                          <td>{formatToTwoDecimal(quantityGrowthIndex)}%</td>
                          <td>{formatToTwoDecimal(amountGrowthIndex)}%</td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CSSTransition>
        </div>

        {/* Parent Navigation Tabs: Stocks and Deficit Stocks */}
        <div className="container-fluid mt-2">
          <Tabs
            transition={false}
            mountOnEnter
            unmountOnExit={false}
            activeKey={mainTab}
            onSelect={(key) => setMainTab(key)}
            id="main-inventory-tabs"
            className="mb-3 custom-font"
          >
            {/* Stocks Parent Tab */}
            <Tab eventKey="Stocks" title="Stocks">
              <div className="mt-2">
                <Tabs
                  transition={false}
                  mountOnEnter
                  unmountOnExit={false}
                  activeKey={stocksTab}
                  onSelect={(key) => setStocksTab(key)}
                  id="stocks-category-tabs"
                  className="mb-3"
                >
                  {constant_productCategory.map((category) => (
                    <Tab key={category} eventKey={category} title={category}>
                      <ProductTab productCategory={category} {...dateRange} />
                    </Tab>
                  ))}
                </Tabs>
              </div>
            </Tab>

            {/* Deficit Stocks Parent Tab */}
            <Tab eventKey="Deficit Stocks" title="Deficit Stocks">
              <div className="mt-2">
                <Tabs
                  transition={false}
                  mountOnEnter
                  unmountOnExit={false}
                  activeKey={deficitTab}
                  onSelect={(key) => setDeficitTab(key)}
                  id="deficit-category-tabs"
                  className="mb-3"
                >
                  {constant_productCategory.map((category) => (
                    <Tab key={category} eventKey={category} title={category}>
                      <ProductDeficitTab
                        productCategory={category}
                        {...dateRange}
                      />
                    </Tab>
                  ))}
                </Tabs>
              </div>
            </Tab>
          </Tabs>
        </div>
      </>
    </div>
  );
};

export default InventoryReport1;
